import React, { useState, useEffect } from 'react';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useIsEditor } from '../hooks/useIsEditor';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Snackbar
} from '@mui/material';
import {
  Person,
  ContactMail,
  CheckCircle,
  Error as ErrorIcon,
  Edit
} from '@mui/icons-material';

const ContactsManagement = ({ 
  open, 
  onClose, 
  account, 
  onUpdate,
  authenticatedFetch,
  API_ENDPOINT 
}) => {
  const isAdmin = useIsAdmin();
  const isEditor = useIsEditor();
  const canEdit = isAdmin || isEditor;
  const [loading, setLoading] = useState(false);
  const [currentContacts, setCurrentContacts] = useState({});
  const [accountOwnerInput, setAccountOwnerInput] = useState('');
  const [technicalContactInput, setTechnicalContactInput] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load current contacts when dialog opens
  useEffect(() => {
    if (open && account) {
      loadCurrentContacts();
    }
  }, [open, account]);

  const loadCurrentContacts = async () => {
    setLoading(true);
    
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/${account.id}/contacts`
      );
      const data = await response.json();
      
      if (data.account_id) {
        setCurrentContacts(data);
        // Pre-populate inputs with current raw User IDs (convert + to , for frontend)
        const accountOwnerRaw = (data.account_owner?.raw || '').replace(/\+/g, ', ');
        const technicalContactRaw = (data.technical_contact?.raw || '').replace(/\+/g, ', ');
        
        setAccountOwnerInput(accountOwnerRaw);
        setTechnicalContactInput(technicalContactRaw);
      } else {
        setSnackbar({ open: true, message: 'Failed to load current contacts', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Error loading contacts: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const validateContacts = async () => {
    if (!accountOwnerInput.trim() && !technicalContactInput.trim()) {
      setValidationResult(null);
      return;
    }

    setValidating(true);

    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/${account.id}/contacts/validate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountOwner: accountOwnerInput.trim(),
            technicalContact: technicalContactInput.trim()
          })
        }
      );

      const data = await response.json();
      setValidationResult(data);
    } catch (err) {
      setSnackbar({ open: true, message: `Error validating contacts: ${err.message}`, severity: 'error' });
    } finally {
      setValidating(false);
    }
  };

  const updateContacts = async () => {
    if (!validationResult || 
        !validationResult.account_owner?.valid || 
        !validationResult.technical_contact?.valid) {
      setSnackbar({ open: true, message: 'Please validate contacts before updating', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/${account.id}/contacts`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountOwner: accountOwnerInput.trim(),
            technicalContact: technicalContactInput.trim()
          })
        }
      );

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: 'Successfully updated Account Owner and Technical Contact', severity: 'success' });
        
        // Reload current contacts
        await loadCurrentContacts();
        
        // Notify parent component
        if (onUpdate) {
          onUpdate();
        }
        
        // Clear validation
        setValidationResult(null);
        
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to update contacts', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Error updating contacts: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAccountOwnerInput('');
    setTechnicalContactInput('');
    setValidationResult(null);
    setSnackbar(prev => ({ ...prev, open: false }));
    onClose();
  };

  const renderValidationResults = (contactType, validation) => {
    if (!validation) return null;

    return (
      <Box sx={{ mt: 1 }}>
        {validation.valid ? (
          <Box>
            {validation.user_ids?.map((user, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CheckCircle color="success" fontSize="small" />
                <Typography variant="body2">
                  <strong>{user.user_id}</strong> → {user.name}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box>
            {validation.errors?.map((error, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                <ErrorIcon color="error" fontSize="small" sx={{ mt: 0.1 }} />
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              </Box>
            ))}
            
            {/* Show suggestions if available */}
            {validation.suggestions && validation.suggestions.length > 0 && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="caption" color="info.dark" sx={{ fontWeight: 'bold' }}>
                  💡 Suggestions:
                </Typography>
                {validation.suggestions.slice(0, 3).map((suggestion, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="caption" color="info.dark">
                      • <strong>{suggestion.user_id}</strong> → {suggestion.name}
                    </Typography>
                  </Box>
                ))}
                <Typography variant="caption" color="info.dark" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                  Try using the User ID instead (e.g., {validation.suggestions[0]?.user_id})
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit color="primary" />
          <Typography variant="h6">
            Manage Account Contacts
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          Account: {account?.name} ({account?.id})
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>Loading contacts...</Typography>
          </Box>
        )}

        {/* Current Contacts Display */}
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem', mb: 0.5, mt: 2 }}>
          Current Contacts
        </Typography>

        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Person color="primary" />
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Account Owner</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Raw: {currentContacts.account_owner?.raw || 'Not set'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            Display: {currentContacts.account_owner?.resolved || 'Not set'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ContactMail color="primary" />
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Technical Contact</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Raw: {currentContacts.technical_contact?.raw || 'Not set'}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            Display: {currentContacts.technical_contact?.resolved || 'Not set'}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Update Contacts Section */}
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem', mb: 0.5 }}>
          Update Contacts
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
            Enter User IDs in format: I70625, I21893, I70794 (comma-separated)
          </Typography>
        </Alert>

        <TextField
          fullWidth
          label="Account Owner User IDs"
          placeholder="I70625"
          value={accountOwnerInput}
          onChange={(e) => setAccountOwnerInput(e.target.value)}
          size="small"
          sx={{
            mb: 2,
            '& .MuiInputBase-input': { fontSize: '0.85rem' },
            '& .MuiFormHelperText-root': { fontSize: '0.75rem' }
          }}
          helperText="Enter Verisk User IDs (e.g., I70625). For multiple IDs, separate with commas."
        />

        <TextField
          fullWidth
          label="Technical Contact User IDs"
          placeholder="I21893, I70794, I71184"
          value={technicalContactInput}
          onChange={(e) => setTechnicalContactInput(e.target.value)}
          size="small"
          sx={{
            mb: 2,
            '& .MuiInputBase-input': { fontSize: '0.85rem' },
            '& .MuiFormHelperText-root': { fontSize: '0.75rem' }
          }}
          helperText="Enter Verisk User IDs (e.g., I21893, I70794). For multiple IDs, separate with commas."
        />

        <Button
          variant="outlined"
          onClick={validateContacts}
          disabled={(!accountOwnerInput.trim() && !technicalContactInput.trim()) || validating}
          sx={{ mb: 2 }}
        >
          {validating ? <CircularProgress size={20} /> : 'Validate User IDs'}
        </Button>

        {/* Validation Results */}
        {validationResult && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem', mb: 0.5 }}>
              Validation Results:
            </Typography>
            
            {accountOwnerInput.trim() && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>Account Owner:</Typography>
                {renderValidationResults('account_owner', validationResult.account_owner)}
              </Box>
            )}
            
            {technicalContactInput.trim() && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>Technical Contact:</Typography>
                {renderValidationResults('technical_contact', validationResult.technical_contact)}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        {canEdit && (
          <Button
            variant="contained"
            onClick={updateContacts}
            disabled={
              loading || 
              !validationResult ||
              !validationResult.account_owner?.valid ||
              !validationResult.technical_contact?.valid
            }
            color="primary"
          >
            {loading ? <CircularProgress size={20} /> : 'Update Contacts'}
          </Button>
        )}
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', fontSize: '0.75rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ContactsManagement;
